import { t } from "i18next";
import { HardDriveUpload } from "lucide-react";
import type { JSX } from "react";
import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "@/hooks/use-toast";
import {
  queuedRecordings,
  recoverableRecordings,
  useOfflineRecordingStore,
} from "@/store/offlineRecording.store";
import { PendingRecordingsList } from "./OfflineRecordingViews";

const report = (error: unknown): void => {
  toast({
    variant: "error",
    title: t("offlineRecording.errors.title", "Recording not changed"),
    description:
      error instanceof Error
        ? error.message
        : t(
            "offlineRecording.errors.storage",
            "The recording on this device could not be updated.",
          ),
  });
};

/** Navbar entry for recordings kept on this device (hidden when there are none). */
export const PendingRecordingsButton = (): JSX.Element | null => {
  const [open, setOpen] = useState(false);
  const recordings = useOfflineRecordingStore((state) => state.recordings);
  const held = useOfflineRecordingStore((state) => state.held);
  const online = useOfflineRecordingStore((state) => state.online);
  const progress = useOfflineRecordingStore((state) => state.progress);
  const recovered = recoverableRecordings(recordings, held);
  const queued = queuedRecordings(recordings);
  const count = recovered.length + queued.length;
  const attention =
    recovered.length > 0 ||
    queued.some(
      (row) =>
        row.status === "uncertain" ||
        (row.status === "failed" && row.upload?.blocked),
    );
  const store = useOfflineRecordingStore.getState;
  const run = (work: Promise<void>): void => {
    work.catch(report);
  };

  if (count === 0 && !open) return null;
  return (
    <>
      <button
        aria-label={t(
          "offlineRecording.pending.open",
          "Pending recordings ({{count}})",
          { count },
        )}
        className="relative flex h-11 w-11 items-center justify-center rounded-md transition-colors hover:bg-muted hover:text-muted-foreground focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary"
        data-testid="pending-recordings-button"
        type="button"
        onClick={() => {
          setOpen(true);
          void store().refresh();
        }}
      >
        <HardDriveUpload className="h-6 w-6 text-primary" />
        {count > 0 && (
          <span
            className={
              attention
                ? "absolute right-0 top-0 flex h-5 min-w-5 items-center justify-center rounded-full bg-error px-1 text-xs font-bold text-white"
                : "absolute right-0 top-0 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-xs font-bold text-primary-foreground"
            }
          >
            {count}
          </span>
        )}
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          className="flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-md sm:rounded-l-2xl"
          data-testid="pending-recordings-sheet"
        >
          <SheetHeader className="space-y-2 px-5 pb-4 pt-6 text-left sm:px-6">
            <SheetTitle className="pr-10 text-xl">
              {t("offlineRecording.pending.title", "Pending recordings")}
            </SheetTitle>
            <SheetDescription className="text-sm leading-6">
              {t(
                "offlineRecording.pending.description",
                "Recordings saved on this device that have not reached ROSC yet. They are removed from the device as soon as the upload is confirmed.",
              )}
            </SheetDescription>
          </SheetHeader>
          <div className="px-5 pb-8 sm:px-6">
            <PendingRecordingsList
              online={online}
              progress={progress}
              queued={queued}
              recovered={recovered}
              onDiscard={(id) => run(store().discard(id))}
              onDownload={(id) => run(store().download(id))}
              onProcess={(id) => {
                setOpen(false);
                store().setProcessing(id);
              }}
              onRetry={(id) => run(store().retry(id))}
            />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
